import React, { useState, useEffect, useRef } from 'react';
import { TextInput, DataListInput } from '../../components/forms/Input';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from '../../api';
import { PrimaryButton, SecondaryButton } from '../../components/forms/buttons';
import "../../assets/css/profile/profile.css";
import "../../assets/css/components/forms/forms.css";

const EditUser = ({ userId, userData, onClose, onSubmit, isOpen }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username:'',
    position: '',
    position_id: '',
    hospital: '',
    hospital_id: '', 
  });

  const [hospitals, setHospitals] = useState([]);
  const [positions, setPositions] = useState([]);
  const [hospitalsList, setHospitalsList] = useState([]); 
  const [positionsList, setPositionsList] = useState([]); 
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const popupRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
      const handleClickOutside = (e) => {
          if (popupRef.current && !popupRef.current.contains(e.target)) {
              onClose(); 
          }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
          document.removeEventListener('mousedown', handleClickOutside);
      };
  }, [onClose]);

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      onClose(); 
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Fetch hospitals and positions
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const token = Cookies.get('accessToken');

        // Fetch hospitals
        const hospitalsResponse = await axios.get(`${API_URL}/hospitals/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Handle both paginated and non-paginated responses
        const hospitalsData = hospitalsResponse.data.results || hospitalsResponse.data;
        if (Array.isArray(hospitalsData)) {
          setHospitalsList(hospitalsData);
          setHospitals(hospitalsData.map(h => h.name || h));
        } else {
          console.error('Unexpected hospitals data format:', hospitalsData);
          setHospitalsList([]);
          setHospitals([]);
        }

        // Fetch positions
        const positionsResponse = await axios.get(`${API_URL}/position/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Handle both paginated and non-paginated responses
        const positionsData = positionsResponse.data.results || positionsResponse.data;
        if (Array.isArray(positionsData)) {
          setPositionsList(positionsData);
          setPositions(positionsData.map(p => p.name || p));
        } else {
          console.error('Unexpected positions data format:', positionsData);
          setPositionsList([]);
          setPositions([]);
        }

        console.log('Loaded options:', {
          hospitals: hospitalsData,
          positions: positionsData,
          hospitalNames: hospitalsData.map(h => h.name || h),
          positionNames: positionsData.map(p => p.name || p)
        });
      } catch (error) {
        console.error('Error fetching options:', error);
        setError('Failed to load hospitals and positions');
        // Set empty arrays as fallback
        setHospitalsList([]);
        setHospitals([]);
        setPositionsList([]);
        setPositions([]);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      console.log('User data received:', userData);
      
  
      setFormData(prev => ({
        ...prev,
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        username: userData.username || '',
        position: userData.position || '', 
        hospital: userData.hospital || '', 
      }));
    }
  }, [userData]);

  const handleHospitalChange = (value) => {
    setFormData(prev => ({
      ...prev,
      hospital: value
    }));
  };

  const handlePositionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      position: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const token = Cookies.get('accessToken');
      setIsLoading(true);

      const selectedHospital = hospitalsList.find(h => h.name === formData.hospital);
      const selectedPosition = positionsList.find(p => p.name === formData.position);

      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        username: formData.username,
        position: formData.position,
        hospital: formData.hospital,
      };

      const response = await axios.put(`${API_URL}/user/update_user/${userId}/`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('API response:', response);
      setSuccessMessage('User updated successfully!');
      if (onSubmit) {
        onSubmit(response.data);
      }
      onClose();
    } catch (error) {
      console.error('API error:', error);
      setError(error.response?.data?.message || 'Failed to update user');
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="overlay">
      <div className="popup-user">
        <form onSubmit={handleSubmit} ref={popupRef}>
          <h2>Edit User</h2>
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          <div className='half'>
            <div className="form-field">
              <label htmlFor="username">Username</label>
              <TextInput
                id="username"
                name="username"
                placeholder="Enter username"
                value={formData.username}
                setValue={(value) => setFormData(prev => ({ ...prev, username: value }))}
              />
            </div>

            <div className="form-field">
              <label htmlFor="first_name">First Name</label>
              <TextInput
                id="first_name"
                name="first_name"
                placeholder="Enter first name"
                value={formData.first_name}
                setValue={(value) => setFormData(prev => ({ ...prev, first_name: value }))}
              />
            </div>
          </div>

          <div className='half'>
            <div className="form-field">
              <label htmlFor="last_name">Last Name</label>
              <TextInput
                id="last_name"
                name="last_name"
                placeholder="Enter last name"
                value={formData.last_name}
                setValue={(value) => setFormData(prev => ({ ...prev, last_name: value }))}
              />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email Address</label>
              <TextInput
                id="email"
                name="email"
                placeholder="Enter email address"
                value={formData.email}
                setValue={(value) => setFormData(prev => ({ ...prev, email: value }))}
              />
            </div>
          </div>

          <div className='half'>
            <div className="form-field">
              <label htmlFor="position">User Role</label>
              <DataListInput
                id="position"
                name="position"
                placeholder="Select user role"
                options={positions}
                value={formData.position}
                setValue={handlePositionChange}
              />
            </div>

            <div className="form-field">
              <label htmlFor="hospital">Hospital/Facility</label>
              <DataListInput
                id="hospital"
                name="hospital"
                placeholder="Select hospital or facility"
                options={hospitals}
                value={formData.hospital}
                setValue={handleHospitalChange}
              />
            </div>
          </div>

          <div className="popup-buttons">
            <SecondaryButton onClick={handleCancel} type="button" isLoading={false} buttonText="Cancel" />
            <PrimaryButton 
              isSubmitting={isSubmitting} 
              buttonText="Save" 
              type="submit" 
              processingText="Editing User" 
              isLoading={isSubmitting} 
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;